/** Client for onix-api. All requests go to /api on the same origin. */

import type { JsonMl } from "@/lib/onix-path";

export type Health = {
  status: "ok" | "error";
  version: string;
  /** Commit of the server's image, empty when unknown. */
  commit: string;
  db: string;
  store: string;
};

export type Role = "viewer" | "editor" | "admin";

export const ROLE_LABELS: Record<Role, string> = {
  viewer: "Lesen",
  editor: "Bearbeiten",
  admin: "Admin",
};

export type Tenant = { id: number; name: string; role: Role };

/** The logged-in user with the current tenant (`GET /api/auth/me`). */
export type Me = {
  user: { id: number; email: string; name: string };
  tenant: Tenant;
  tenants: Tenant[];
};

export type Member = { user_id: number; email: string; name: string; role: Role; since: string };
export type Invitation = { id: number; email: string; role: Role; expires_at: string };
export type Members = { members: Member[]; invitations: Invitation[] };

/** What an invitation link offers (`GET /api/auth/invitation`). */
export type InvitationInfo = {
  email: string;
  tenant: string;
  role: Role;
  account_exists: boolean;
};

/** An error answer: HTTP status, stable code and a German message for the user. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code = "",
  ) {
    super(message);
  }
}

/**
 * Sends a request and returns the JSON answer (or `undefined` for 204).
 * Error answers become an {@link ApiError}; `accept` lists statuses whose
 * body is returned anyway (a 503 from /api/health still carries one).
 */
export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  accept: number[] = [],
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const response = await fetch(`/api${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok && !accept.includes(response.status)) {
    const error = (await response.json().catch(() => null)) as {
      error?: string;
      message?: string;
    } | null;
    throw new ApiError(
      response.status,
      error?.message ?? `${response.status} ${response.statusText}`,
      error?.error,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const getJson = <T>(path: string, accept: number[] = []) =>
  request<T>("GET", path, undefined, accept);

export const fetchHealth = () => getJson<Health>("/health", [503]);

/** The logged-in user, or `null` without a valid session. */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await getJson<Me>("/auth/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export const login = (email: string, password: string) =>
  request<Me>("POST", "/auth/login", { email, password });
export const logout = () => request<void>("POST", "/auth/logout");
export const switchTenant = (tenantId: number) =>
  request<Me>("POST", "/auth/tenant", { tenant_id: tenantId });
export const requestPasswordReset = (email: string) =>
  request<void>("POST", "/auth/password-reset", { email });
export const confirmPasswordReset = (token: string, password: string) =>
  request<void>("POST", "/auth/password-reset/confirm", { token, password });
export const fetchInvitation = (token: string) =>
  getJson<InvitationInfo>(`/auth/invitation?token=${encodeURIComponent(token)}`);
export const acceptInvitation = (token: string, password: string, name?: string) =>
  request<Me>("POST", "/auth/invitation", { token, password, name: name ?? "" });

export const fetchMembers = () => getJson<Members>("/members");
export const inviteMember = (email: string, role: Role) =>
  request<Invitation>("POST", "/members/invitations", { email, role });
export const cancelInvitation = (id: number) =>
  request<void>("DELETE", `/members/invitations/${id}`);
export const changeRole = (userId: number, role: Role) =>
  request<void>("PATCH", `/members/${userId}`, { role });
export const removeMember = (userId: number) => request<void>("DELETE", `/members/${userId}`);

/** What happens to titles missing from the file (B7): "Ergänzen" or "Ersetzen". */
export type ImportMode = "merge" | "replace";

/** A job of the queue (`GET /api/jobs/{id}`, B5). Kind `validate` is a dry run (B7). */
export type Job = {
  id: number;
  kind: string;
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  payload: {
    file_name?: string;
    size?: number;
    upload?: number;
    mode?: ImportMode;
    /** Import after a preview: the preview job and the deletions it showed. */
    preview?: number;
    max_deletions?: number;
    /** Export job (B12): the run. */
    export?: number;
    /** Media job (B13): `new`, `all` or `evaluate`. */
    scope?: MediaScope;
    /** Import (B13): fetch the media links afterwards. */
    fetch_media?: boolean;
  };
  progress: {
    phase?: "waiting" | "check" | "merge" | "write" | "upload" | "sync";
    /** Export job: titles in the selection. */
    total?: number;
    bytes?: number;
    total_bytes?: number;
    documents?: number;
    products?: number;
  } & Partial<MediaCounts>;
  result:
    | ({ mode?: ImportMode; dry_run?: boolean; documents?: ImportedDocument[] } & Partial<
        MediaCounts & { urls: number }
      >)
    | null;
  error: string | null;
  attempts: number;
  cancel_requested: boolean;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

/** One document of an import job's result. */
export type ImportedDocument = {
  file_name: string;
  import_job: number;
  onix_version: string;
  products: number;
  created: number;
  changed: number;
  unchanged: number;
  skipped: number;
  /** Deleted (in a dry run: would be deleted), by delete record or mode replace. */
  deleted?: number;
  /** Delete records for titles not in the catalogue. */
  not_found?: number;
  /** The first 1000 deleted titles. */
  deleted_titles?: DeletedTitle[];
  errors: number;
  warnings: number;
  products_with_errors: number;
  seconds: number;
};

export type DeletedTitle = {
  record_reference: string;
  isbn13: string | null;
  title: string | null;
};

export const fetchJob = (id: number) => getJson<Job>(`/jobs/${id}`);
export const cancelJob = (id: number) => request<Job>("POST", `/jobs/${id}/cancel`);
/** Turns the preview of "Ersetzen" into the import (B7). */
export const confirmJob = (id: number) => request<Job>("POST", `/jobs/${id}/confirm`);

/** The newest jobs first; `next` goes into `before` for the older ones (B9). */
export const fetchJobs = (before?: number) =>
  getJson<{ jobs: Job[]; next: number | null }>(
    `/jobs?limit=50${before === undefined ? "" : `&before=${before}`}`,
  );

export type Severity = "error" | "warning" | "info";
export type Stage = "xml" | "xsd" | "codelist" | "normalize" | "rule";

/** Counts of a job's report for its filters (`/api/jobs/{id}/report`, UI-07). */
export type ReportSummary = {
  issues: number;
  severities: { value: Severity; count: number }[];
  stages: { value: Stage; count: number }[];
  rules: { value: string; count: number }[];
  deletions: number;
};

/** An issue the import found, as it was at the time of the job. */
export type ReportIssue = {
  id: number;
  import_job: number;
  file_name: string;
  product_index: number | null;
  record_reference: string | null;
  /** The title in the catalogue, if it is there (B10). */
  product_id: number | null;
  isbn13: string | null;
  title: string | null;
  stage: Stage;
  severity: Severity;
  rule: string | null;
  path: string | null;
  line: number;
  column: number | null;
  message: string;
};

export type IssueFilter = { severity?: Severity; stage?: Stage; rule?: string };

/** Query string of the filter, also for the CSV link. */
export function issueQuery(filter: IssueFilter) {
  const params = new URLSearchParams();
  for (const key of ["severity", "stage", "rule"] as const) {
    const value = filter[key];
    if (value) params.set(key, value);
  }
  return params.toString();
}

export const fetchReport = (id: number) => getJson<ReportSummary>(`/jobs/${id}/report`);
export const fetchIssues = (id: number, filter: IssueFilter, after?: string) => {
  const query = issueQuery(filter);
  const page = `limit=200${after ? `&after=${encodeURIComponent(after)}` : ""}`;
  return getJson<{ issues: ReportIssue[]; next: string | null; total: number | null }>(
    `/jobs/${id}/issues?${query ? `${query}&` : ""}${page}`,
  );
};
export type ReportDeletion = DeletedTitle & {
  id: number;
  import_job: number;
  file_name: string;
  product_id: number | null;
};
export const fetchDeletions = (id: number, after?: number) =>
  getJson<{ deletions: ReportDeletion[]; next: number | null }>(
    `/jobs/${id}/deletions?limit=200${after === undefined ? "" : `&after=${after}`}`,
  );
/** Links for the browser to download (with the session cookie). */
export const issuesCsvUrl = (id: number, filter: IssueFilter) => {
  const query = issueQuery(filter);
  return `/api/jobs/${id}/issues.csv${query ? `?${query}` : ""}`;
};
export const deletionsCsvUrl = (id: number) => `/api/jobs/${id}/deletions.csv`;

/** A Prio 1 rule and whether the tenant has it on (`/api/rules`, B7, VAL-03). */
export type Rule = {
  id: string;
  title: string;
  severity: "error" | "warning" | "info";
  enabled: boolean;
};

export const fetchRules = () => getJson<{ rules: Rule[] }>("/rules");
export const switchRule = (id: string, enabled: boolean) =>
  request<{ rules: Rule[] }>("PUT", `/rules/${id}`, { enabled });

/** An upload from the browser (`/api/uploads`, B6). */
export type Upload = {
  id: number;
  file_name: string;
  size: number;
  part_size: number;
  parts_total: number;
  parts_done: number[];
  status: "uploading" | "complete" | "aborted" | "expired";
  job_id: number | null;
  created_at: string;
  updated_at: string;
};

export const fetchOpenUploads = () => getJson<{ uploads: Upload[] }>("/uploads");
export const abortUpload = (id: number) => request<void>("DELETE", `/uploads/${id}`);

/** Sortable columns of the catalogue (`/api/products`, B8). */
export type CatalogSort = "title" | "contributors" | "isbn13" | "publishing_date" | "updated_at";

/**
 * Search, filters and order of the catalogue table. Lists are
 * alternatives; different filters all apply. Dates: `2026`, `2026-03` or
 * `2026-03-01`.
 */
export type CatalogFilter = {
  q?: string;
  validation?: string[];
  status?: string[];
  form?: string[];
  publisher?: string[];
  from?: string;
  to?: string;
  /** Worst state of the media links (B13): error, warning, unchecked, ok, none. */
  media?: string[];
  sort?: CatalogSort;
  dir?: "asc" | "desc";
};

export type CatalogTitle = {
  id: number;
  record_reference: string;
  isbn13: string | null;
  title: string | null;
  subtitle: string | null;
  contributors: string | null;
  series: string | null;
  publisher: string | null;
  product_form: string | null;
  publishing_date: string | null;
  publishing_status: string | null;
  validation_status: "ok" | "warning" | "error";
  /** Worst state of the media links; `null` without links (B13). */
  media_status: MediaStatus | null;
  updated_at: string;
};

/** One page; `next` continues it, `total` only on the first page. */
export type CatalogPage = { titles: CatalogTitle[]; next: string | null; total: number | null };

export const CATALOG_PAGE = 100;

/** Query string for `/api/products`; repeated keys for several values. */
export function catalogQuery(filter: CatalogFilter, after?: string): string {
  const params = new URLSearchParams();
  if (filter.q?.trim()) params.set("q", filter.q.trim());
  for (const key of ["validation", "status", "form", "publisher", "media"] as const) {
    for (const value of filter[key] ?? []) params.append(key, value);
  }
  if (filter.from) params.set("from", filter.from);
  if (filter.to) params.set("to", filter.to);
  if (filter.sort) params.set("sort", filter.sort);
  if (filter.dir) params.set("dir", filter.dir);
  params.set("limit", String(CATALOG_PAGE));
  if (after) params.set("after", after);
  return params.toString();
}

export const fetchCatalog = (filter: CatalogFilter, after?: string) =>
  getJson<CatalogPage>(`/products?${catalogQuery(filter, after)}`);

/** A filter value and how many titles have it. */
export type FacetCount = { value: string; count: number };
export type Facets = {
  total: number;
  validation_status: FacetCount[];
  publishing_status: FacetCount[];
  product_form: FacetCount[];
  publisher: FacetCount[];
  media_status: FacetCount[];
};

export const fetchFacets = () => getJson<Facets>("/products/facets");

/** An EDItEUR code list with the English texts (`/api/codelists/{n}`). */
export type Codelist = {
  number: number;
  description: string;
  issue: number;
  codes: { value: string; description: string; deprecated: boolean }[];
};

export const fetchCodelist = (number: number) => getJson<Codelist>(`/codelists/${number}`);

/** Code lists of the coded fields of a tree and the texts of its codes. */
export type TreeCodes = {
  /** List number by element name, `@name` for attributes. */
  lists: Record<string, number>;
  /** English EDItEUR description by `list:code`. */
  labels: Record<string, string>;
};

/** An issue of the latest check of a title (import or edit). */
export type ProductIssue = {
  id: number;
  stage: Stage;
  severity: Severity;
  rule: string | null;
  path: string | null;
  line: number;
  column: number | null;
  message: string;
};

/**
 * An earlier tree: an import replaced or deleted it (`import_job`), or
 * `user` saved an edit (B11).
 */
export type Revision = {
  version: number;
  source: "import" | "edit" | "bulk";
  import_job: number | null;
  job: number | null;
  user: string | null;
  created_at: string;
};

/** One title (`GET /api/products/{id}`, B10). */
export type ProductDetail = {
  id: number;
  record_reference: string;
  version: number;
  isbn13: string | null;
  title: string | null;
  subtitle: string | null;
  contributors: string | null;
  validation_status: "ok" | "warning" | "error";
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  last_import_job: number | null;
  last_job: number | null;
  /** Who saved the current tree in the editor; `null` after an import. */
  updated_by: string | null;
  onix: JsonMl;
  issues: ProductIssue[];
  revisions: Revision[];
  codes: TreeCodes;
  /** The media links with their check (B13). */
  media: MediaItem[];
};

export type RevisionTree = {
  version: number;
  source: Revision["source"];
  created_at: string;
  onix: JsonMl;
  codes: TreeCodes;
};

export const fetchProduct = (id: number) => getJson<ProductDetail>(`/products/${id}`);
export const fetchRevision = (id: number, version: number) =>
  getJson<RevisionTree>(`/products/${id}/revisions/${version}`);

/** What a check of an edited tree found (`POST /api/products/{id}/check`). */
export type CheckResult = {
  validation_status: ProductDetail["validation_status"];
  issues: Omit<ProductIssue, "id" | "line" | "column">[];
  codes: TreeCodes;
};

export const checkProduct = (id: number, onix: JsonMl) =>
  request<CheckResult>("POST", `/products/${id}/check`, { onix });

/** Saves an edited tree started from `version`; 409 `version_conflict` if it changed since. */
export const saveProduct = (id: number, onix: JsonMl, version: number) =>
  request<ProductDetail>("PUT", `/products/${id}`, { onix, version });

/** What an ONIX 3.1 element holds (`GET /api/structure`, B11). */
export type ElementKind =
  "composite" | "text" | "integer" | "decimal" | "code" | "xhtml" | "uri" | "empty";

export type ElementInfo = {
  kind: ElementKind;
  /** Code list of a coded element. */
  list?: number;
  /** Several codes separated by spaces. */
  multiple?: boolean;
  attributes?: { name: string; list?: number }[];
  /** Possible children in XSD order; `max: null` is unbounded. */
  children?: { name: string; min: number; max: number | null }[];
};

export type Structure = Record<string, ElementInfo>;

export const fetchStructure = () => getJson<Structure>("/structure");

/** Message for the user from any error. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Keine Verbindung zum Server. Bitte später erneut versuchen.";
}

/** As in onix-api (`auth::MIN_PASSWORD_CHARS`). */
export const MIN_PASSWORD_CHARS = 10;

/** Which titles an export takes: the filters of the catalogue without order (B12). */
export type ExportFilter = Omit<CatalogFilter, "sort" | "dir">;

/** The filters of the catalogue address, empty values left out. */
export function exportFilter(filter: CatalogFilter): ExportFilter {
  const out: ExportFilter = {};
  if (filter.q?.trim()) out.q = filter.q.trim();
  for (const key of ["validation", "status", "form", "publisher", "media"] as const) {
    if (filter[key]?.length) out[key] = filter[key];
  }
  if (filter.from) out.from = filter.from;
  if (filter.to) out.to = filter.to;
  return out;
}

export type ExportHeader = {
  sender_name: string;
  contact_name: string;
  email: string;
  addressee_name: string;
};

/** What an export writes (`onix_db::export::Settings`). */
export type ExportSettings = {
  version: "3.0" | "3.1";
  tags: "reference" | "short";
  compression: "none" | "gzip" | "zip";
  /** Pattern with {tenant} {profile} {date} {time} {version} {tags}. */
  file_name: string;
  filter: ExportFilter;
  header: ExportHeader;
  xsd_check: boolean;
  /** MED-03: the public links of stored copies instead of the originals. */
  media_links: "original" | "own";
};

export type ExportProfile = {
  id: number;
  name: string;
  settings: ExportSettings;
  updated_at: string;
};

/** An element the export to 3.0 removed, with the first titles concerned. */
export type ExportDropped = { element: string; count: number; examples: string[] };

export type ExportReport = {
  bytes: number;
  dropped: ExportDropped[];
  dropped_products: number;
  xsd_checked: boolean;
  xsd_errors: number;
  xsd_issues: { record_reference: string | null; path: string | null; message: string }[];
  unreadable: string[];
  /** With own media links: how many were replaced, how many stayed. */
  media_links?: { own: number; original: number } | null;
};

/** One export run (`GET /api/exports/{id}`). */
export type ExportRun = {
  id: number;
  job_id: number | null;
  profile_id: number | null;
  /** `null` for an export of a catalogue selection. */
  profile_name: string | null;
  settings: ExportSettings;
  status: Job["status"];
  progress: { phase?: "waiting" | "write" | "upload"; products?: number; total?: number } | null;
  file_name: string | null;
  file_size: number | null;
  products: number | null;
  skipped: number | null;
  report: ExportReport | null;
  failure: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** When the file was deleted after the retention (B14). */
  expired_at: string | null;
};

export const fetchExportProfiles = () =>
  getJson<{ profiles: ExportProfile[]; defaults: ExportSettings }>("/export-profiles");
export const createExportProfile = (name: string, settings: ExportSettings) =>
  request<ExportProfile>("POST", "/export-profiles", { name, settings });
export const updateExportProfile = (id: number, name: string, settings: ExportSettings) =>
  request<ExportProfile>("PUT", `/export-profiles/${id}`, { name, settings });
export const deleteExportProfile = (id: number) =>
  request<{ deleted: number }>("DELETE", `/export-profiles/${id}`);

/** Starts a run from a profile or from settings (a catalogue selection). */
export const startExport = (from: { profile_id: number } | { settings: ExportSettings }) =>
  request<ExportRun>("POST", "/exports", from);
export const fetchExports = (before?: number) =>
  getJson<{ exports: ExportRun[]; next: number | null }>(
    `/exports?limit=50${before === undefined ? "" : `&before=${before}`}`,
  );
export const fetchExport = (id: number) => getJson<ExportRun>(`/exports/${id}`);
/** Redirects to a signed link of the file; the browser downloads it. */
export const exportDownloadUrl = (id: number) => `/api/exports/${id}/download`;

/** Check state of a media link (B13). */
export type MediaStatus = "unchecked" | "ok" | "warning" | "error";
/** `new`: links not checked yet (or without a copy), `all`, `evaluate`: judge again without requests. */
export type MediaScope = "new" | "all" | "evaluate";

/** Progress and result of a media job. */
export type MediaCounts = {
  synced: number;
  done: number;
  total: number;
  ok: number;
  warning: number;
  error: number;
  stored: number;
  unchanged: number;
};

export type MediaFinding = { severity: "error" | "warning"; code: string; message: string };

/** One media link of a title (`onix_db::media::Item`). */
export type MediaItem = {
  id: number;
  product_id: number;
  record_reference: string;
  isbn13: string | null;
  title: string | null;
  url: string;
  /** List 158, e.g. `01` front cover. */
  content_type: string | null;
  /** List 159, e.g. `03` image. */
  mode: string | null;
  /** List 161, e.g. `02` downloadable file. */
  form: string | null;
  /** The `ResourceLink` field, for `/titel/{id}?feld=…`. */
  path: string | null;
  declared: { format?: string; width?: number; height?: number; bytes?: number };
  check_status: MediaStatus;
  findings: MediaFinding[];
  checked_at: string | null;
  http_status: number | null;
  mime_type: string | null;
  format: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  stored: boolean;
  stored_at: string | null;
  stored_size: number | null;
  /** `<token>/<name>` of the copy's public link (MED-03). */
  public_path: string | null;
};

/** `GET /api/media`: counts of the links and the newest media jobs. */
export type MediaSummary = {
  links: number;
  unchecked: number;
  ok: number;
  warning: number;
  error: number;
  stored: number;
  titles: number;
  titles_with_links: number;
  titles_with_errors: number;
  titles_without_cover: number;
  findings: { code: string; severity: "error" | "warning"; count: number }[];
  min_pixels: number;
  jobs: Job[];
};

export type MediaFilter = { status?: MediaStatus[]; code?: string; stored?: boolean };

export const fetchMediaSummary = () => getJson<MediaSummary>("/media");
export function fetchMediaLinks(filter: MediaFilter, after?: number) {
  const params = new URLSearchParams();
  for (const s of filter.status ?? []) params.append("status", s);
  if (filter.code) params.set("code", filter.code);
  if (filter.stored !== undefined) params.set("stored", String(filter.stored));
  if (after) params.set("after", String(after));
  return getJson<{ links: MediaItem[]; next: number | null }>(`/media/links?${params}`);
}
export const startMediaJob = (kind: "check" | "fetch", scope: "new" | "all") =>
  request<{ job: Job }>("POST", `/media/${kind}`, { scope });
export const saveMediaSettings = (minPixels: number) =>
  request<{ min_pixels: number; job: Job | null }>("PUT", "/media/settings", {
    min_pixels: minPixels,
  });
/** The stored copy; the API answers with a redirect to S3. */
export const mediaFileUrl = (id: number) => `/api/media/${id}/file`;
/** Public link of a stored copy, `/m/<token>/<name>`, for use outside the app. */
export const mediaPublicUrl = (path: string) => `${window.location.origin}/m/${path}`;
